import React, { useState, useEffect, useMemo, useCallback } from "react";
import Icon from "../../../../components/AppIcon";
import {
  calculateForEmployees,
  getEligibilitySummary,
  toEligibilityEmployee,
  buildJobRolesMap,
  EligibilityReasonLabels,
} from "../../../../utils/eligibilityCalculator";
import { useEmployees } from "../../../../hooks/useEmployees";
import { useJobRoles } from "../../../../hooks/useJobRoles";
import { eligibilityCriteriaApi } from "../../../../services/api";

/**
 * Converts the UI settings form to the EligibilityRules shape used by the calculator.
 */
function settingsToRules(settings) {
  return {
    requireEmployeeIsActive: true,
    requireEmployeeIsEligible: true,
    requireRoleIsEligible: true,
    requireRoleIsActive: true,
    excludeEmployeesInProbation: settings.excludeProbation ?? false,
    excludeManagementRoles: settings.excludeManagement ?? false,
 requireMinimumServiceDays: (settings.minimumTenureDays ?? 0) > 0,
    minimumServiceDays: settings.minimumTenureDays ?? 0,
    requireMinimumHoursInPeriod: (settings.minimumHoursPerWeek ?? 0) > 0,
    minimumHoursInPeriod: settings.minimumHoursPerWeek ?? 0,
    requireEmploymentStarted: true,
    excludeTerminatedEmployees: true,
  };
}

/**
 * Converts a version DTO to the settings shape.
 */
function versionToSettings(v) {
  if (!v) return null;
  return {
    excludeProbation: v.excludeEmployeesInProbation ?? false,
    excludeManagement: v.excludeManagementRoles ?? false,
  minimumTenureDays: v.minimumServiceDays ?? 0,
    minimumHoursPerWeek: v.minimumHoursInPeriod ?? 0,
    includePartTime: v.includePartTime ?? true,
    includeCasual: v.includeCasual ?? true,
    probationPeriodDays: 90,
  };
}

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const daysSince = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
};

/**
 * EligibilityTester – live panel showing which employees pass/fail the current eligibility rules.
 *
 * Props:
 *  - settings: the current UI settings form values (same shape as parent state)
 */
const EligibilityTester = ({ settings }) => {
  const [filter, setFilter] = useState("all"); // 'all' | 'eligible' | 'ineligible'
  const [search, setSearch] = useState("");
  const [testDate, setTestDate] = useState(
    new Date().toISOString().split("T")[0],
  );

  // State for the effective eligibility criteria for the selected date
  const [dateSettings, setDateSettings] = useState(null);
  const [dateSettingsLoading, setDateSettingsLoading] = useState(false);

  // Load all employees (not just eligible ones) so we can test the rules against everyone
  const { employees: apiEmployees, loading: employeesLoading } = useEmployees({
    autoFetch: true,
    activeOnly: false,
    troncEligibleOnly: false,
  });

  // Load job roles for lookup
  const { jobRoles, loading: rolesLoading } = useJobRoles({
    autoFetch: true,
    activeOnly: false,
  });

  const loading = employeesLoading || rolesLoading;

  // Build job roles map
  const jobRolesMap = useMemo(() => buildJobRolesMap(jobRoles || []), [jobRoles]);

  // Build a lookup: jobRoleId -> jobRole API data (for extra fields like department)
  const jobRoleLookup = useMemo(() => {
    const map = {};
  (jobRoles || []).forEach((r) => {
   map[r.id] = r;
    });
 return map;
  }, [jobRoles]);

  // Fetch the eligibility criteria effective on the selected test date
  const fetchDateSettings = useCallback(async () => {
    setDateSettingsLoading(true);
    try {
      const res = await eligibilityCriteriaApi.getForDate(testDate);
      if (res.success && res.data) {
        setDateSettings(versionToSettings(res.data));
      } else {
   // No version for that date — fall back to null (will use parent settings as fallback)
        setDateSettings(null);
      }
    } catch (err) {
      console.debug("Failed to load eligibility criteria for test date:", err);
      setDateSettings(null);
    } finally {
    setDateSettingsLoading(false);
    }
  }, [testDate]);

  useEffect(() => {
    fetchDateSettings();
  }, [fetchDateSettings]);

  // Use the date-specific settings if available, otherwise fall back to the parent-provided settings
  const effectiveSettings = dateSettings ?? settings;

  // Run eligibility calculator on every settings change
  const results = useMemo(() => {
    if (!apiEmployees || apiEmployees.length === 0) return [];

    const rules = settingsToRules(effectiveSettings);
    const eligEmployees = apiEmployees.map(toEligibilityEmployee);

  return calculateForEmployees(eligEmployees, jobRolesMap, {
   date: testDate,
      rules,
      clockRecords: [], // We don't have clock records in this context
    });
  }, [apiEmployees, jobRolesMap, effectiveSettings, testDate]);

  // Summary stats
  const summary = useMemo(() => getEligibilitySummary(results), [results]);

  // Filtered + searched results
  const displayResults = useMemo(() => {
    let filtered = results;

    if (filter === "eligible") {
      filtered = filtered.filter((r) => r.isEligible);
    } else if (filter === "ineligible") {
      filtered = filtered.filter((r) => !r.isEligible);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((r) =>
      r.employeeName?.toLowerCase().includes(q),
      );
    }

    return filtered;
  }, [results, filter, search]);

  // Enrich results with employee & role data for display
  const enrichedResults = useMemo(() => {
 const empLookup = {};
    (apiEmployees || []).forEach((e) => {
      empLookup[e.id] = e;
    });

    return displayResults.map((r) => {
      const emp = empLookup[r.employeeId] || {};
      const roleId = emp.primaryJobRoleId || emp.jobRoleId;
      const role = roleId ? jobRoleLookup[roleId] : null;
   return { ...r, emp, role };
    });
  }, [displayResults, apiEmployees, jobRoleLookup]);

  if (loading) {
    return (
      <div className="card p-8 text-center">
        <div className="spinner mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">Loading employees…</p>
      </div>
    );
  }

  if (!apiEmployees || apiEmployees.length === 0) {
    return (
      <div className="card p-8 text-center">
      <Icon
      name="Users"
          size={32}
       className="mx-auto mb-3 text-muted-foreground"
        />
        <p className="text-sm text-muted-foreground">
    No employees found. Import or sync employees first.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
   <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="FlaskConical" size={20} className="text-primary" />
        </div>
     <div>
          <h3 className="font-semibold">Eligibility Rule Tester</h3>
          <p className="text-xs text-muted-foreground">
      Live preview — changes apply as you adjust settings
      </p>
        </div>
      </div>

      {/* Test Date */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
    Test Date:
        </label>
        <input
     type="date"
 value={testDate}
      onChange={(e) => setTestDate(e.target.value)}
    className="input input-sm flex-1"
    />
        {dateSettingsLoading && <div className="spinner spinner-sm" />}
    </div>
      {dateSettings && (
        <div className="text-xs text-muted-foreground bg-success/5 border border-success/20 rounded px-2 py-1">
      <Icon name="ShieldCheck" size={12} className="inline text-success mr-1" />
    Using eligibility criteria effective on {testDate}
 </div>
      )}
  {!dateSettings && !dateSettingsLoading && (
        <div className="text-xs text-muted-foreground bg-muted rounded px-2 py-1">
<Icon name="Info" size={12} className="inline text-muted-foreground mr-1" />
       No specific criteria for this date — using current settings
        </div>
      )}

   {/* Summary Badges */}
      <div className="grid grid-cols-3 gap-2">
        <button
onClick={() => setFilter("all")}
    className={`p-3 rounded-lg border text-center transition-colors ${
  filter === "all"
   ? "bg-primary/10 border-primary/30"
              : "bg-muted/50 border-border hover:bg-muted"
          }`}
        >
          <p className="text-lg font-bold">{summary.totalEmployees}</p>
          <p className="text-xs text-muted-foreground">Total</p>
        </button>
        <button
          onClick={() => setFilter("eligible")}
          className={`p-3 rounded-lg border text-center transition-colors ${
            filter === "eligible"
         ? "bg-success/10 border-success/30"
              : "bg-muted/50 border-border hover:bg-muted"
          }`}
        >
          <p className="text-lg font-bold text-success">
        {summary.eligibleCount}
          </p>
          <p className="text-xs text-muted-foreground">Eligible</p>
        </button>
        <button
       onClick={() => setFilter("ineligible")}
    className={`p-3 rounded-lg border text-center transition-colors ${
            filter === "ineligible"
? "bg-error/10 border-error/30"
       : "bg-muted/50 border-border hover:bg-muted"
      }`}
   >
          <p className="text-lg font-bold text-error">
            {summary.ineligibleCount}
     </p>
          <p className="text-xs text-muted-foreground">Ineligible</p>
  </button>
      </div>

      {/* Reason breakdown (only when showing ineligible) */}
      {(filter === "all" || filter === "ineligible") &&
        summary.ineligibleCount > 0 && (
          <div className="p-3 bg-muted/50 rounded-lg space-y-1">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
     Exclusion Reasons
         </p>
  {Object.entries(summary.reasonCounts)
 .sort((a, b) => b[1] - a[1])
  .map(([code, count]) => (
    <div
        key={code}
 className="flex items-center justify-between text-xs"
                >
 <span className="text-muted-foreground truncate mr-2">
  {EligibilityReasonLabels[code] || code}
  </span>
       <span className="font-semibold text-error shrink-0">
       {count}
  </span>
        </div>
     ))}
          </div>
        )}

      {/* Search */}
      <div className="relative">
    <Icon
   name="Search"
          size={14}
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="text"
          placeholder="Search employees…"
          value={search}
     onChange={(e) => setSearch(e.target.value)}
          className="input input-sm pl-8 w-full"
        />
      </div>

      {/* Employee List */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
        {enrichedResults.length === 0 && (
   <div className="p-6 text-center text-sm text-muted-foreground">
            No employees match the current filter.
          </div>
        )}

        {enrichedResults.map((r) => {
          const emp = r.emp;
     const role = r.role;
          const tenure = daysSince(emp.startDate);

          return (
            <div
   key={r.employeeId}
 className={`p-3 rounded-lg border transition-colors ${
      r.isEligible
            ? "bg-success/5 border-success/20"
          : "bg-error/5 border-error/20"
      }`}
            >
              {/* Name + Status */}
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Icon
       name={r.isEligible ? "CheckCircle" : "XCircle"}
           size={14}
    className={
                 r.isEligible
         ? "text-success shrink-0"
         : "text-error shrink-0"
  }
           />
       <span className="font-medium text-sm truncate">
              {r.employeeName}
        </span>
          </div>
        {!emp.isActive && (
        <span className="badge badge-neutral text-xs shrink-0">
          Inactive
 </span>
          )}
  </div>

     {/* Details grid */}
       <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground ml-5">
   {/* Job Role */}
    <div className="flex items-center gap-1">
              <Icon name="Briefcase" size={10} />
         <span className="truncate">
 {role?.name || "No role"}
        </span>
                </div>

     {/* Department */}
      <div className="flex items-center gap-1">
           <Icon name="Building2" size={10} />
      <span className="truncate">
        {role?.department || emp.department || "—"}
      </span>
     </div>

    {/* Start Date + Tenure */}
                <div className="flex items-center gap-1">
          <Icon name="Calendar" size={10} />
       <span>
        {formatDate(emp.startDate)}
             {tenure !== null && (
              <span className="text-foreground font-medium">
       {" "}
       ({tenure}d)
             </span>
       )}
        </span>
     </div>

         {/* Hours */}
              <div className="flex items-center gap-1">
        <Icon name="Clock" size={10} />
  <span>
 {(emp.contractedHoursPerWeek ?? 0) > 0
    ? `${emp.contractedHoursPerWeek}h/wk (${(emp.contractedHoursPerWeek / 5).toFixed(1)}h/day)`
    : <span className="text-warning">0h/wk (not set)</span>
        }
        </span>
          </div>

         {/* Probation */}
                {emp.probationPeriodDays > 0 && (
                  <div className="flex items-center gap-1 col-span-2">
        <Icon name="Timer" size={10} />
       <span>
   Probation: {emp.probationPeriodDays}d
      {emp.hasPassedProbation ? (
         <span className="text-success ml-1">✓ passed</span>
       ) : (
 <span className="text-warning ml-1">in progress</span>
              )}
         </span>
  </div>
      )}

       {/* Management flag */}
   {role?.isManagement && (
   <div className="flex items-center gap-1 col-span-2">
        <Icon name="Crown" size={10} />
       <span className="text-warning">Management role</span>
      </div>
            )}
              </div>

{/* Failure Reasons */}
            {!r.isEligible && r.reasons.length > 0 && (
     <div className="mt-2 ml-5 space-y-0.5">
  {r.reasons.map((reason, i) => (
      <div
  key={i}
          className="flex items-start gap-1.5 text-xs text-error"
          >
  <Icon
              name="AlertCircle"
              size={10}
         className="mt-0.5 shrink-0"
          />
          <span>
        {reason.description}
         {reason.details && (
     <span className="text-error/70 ml-1">
  ({reason.details})
         </span>
  )}
      </span>
    </div>
      ))}
    </div>
        )}
            </div>
       );
    })}
      </div>

   {/* Footer info */}
      <div className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
        Showing {enrichedResults.length} of {results.length} employees •
        Testing against {formatDate(testDate)}
      </div>
    </div>
  );
};

export default EligibilityTester;
